<template>
  <div
    class="update-required-modal"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="update-required-title"
    aria-describedby="update-required-message"
  >
    <!-- ─── Icon ──────────────────────────────────────────────────── -->
    <div class="update-required-modal__icon-ring" aria-hidden="true">
      <ion-icon :icon="cloudDownloadOutline" />
    </div>

    <!-- ─── Title + message ───────────────────────────────────────── -->
    <h2 id="update-required-title" class="update-required-modal__title">Update available</h2>
    <p id="update-required-message" class="update-required-modal__message">{{ message }}</p>

    <!-- ─── Actions ───────────────────────────────────────────────── -->
    <div class="update-required-modal__actions">
      <button
        v-if="!blocking"
        type="button"
        class="update-required-modal__btn update-required-modal__btn--outline"
        :disabled="pending"
        @click="emit('later')"
      >
        Later
      </button>
      <button
        type="button"
        class="update-required-modal__btn update-required-modal__btn--primary"
        :disabled="pending"
        @click="emit('update')"
      >
        <ion-spinner v-if="pending" name="crescent" class="update-required-modal__spinner" />
        <span v-else>Update now</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * "A newer build is deployed" dialog, raised by the version watchdog.
 *
 * Stateless by design (same discipline as InstallAppModal / SurveyPromptModal):
 * it emits intents and never touches storage, caches or the service worker —
 * `UpdateRequiredHost.vue` owns all of that.
 */
import { computed } from 'vue';
import { IonIcon, IonSpinner } from '@ionic/vue';
import { cloudDownloadOutline } from 'ionicons/icons';

const props = withDefaults(
  defineProps<{
    /** Version this bundle was built as. */
    currentVersion: string;
    /** Version the server reports, when the fetch resolved it. */
    newVersion: string | null;
    /** Hide "Later" — the future forced-update switch (see the host). */
    blocking?: boolean;
    /** Disables the buttons and shows a spinner while the reload is set up. */
    pending?: boolean;
  }>(),
  { blocking: false, pending: false },
);

const emit = defineEmits<{
  (e: 'update'): void;
  (e: 'later'): void;
}>();

const message = computed(() => {
  const target = props.newVersion ? ` (v${props.newVersion})` : '';
  return (
    `A new version of Syntry${target} is ready. You're on v${props.currentVersion}. ` +
    'Updating clears the app cache and reloads — takes a few seconds.'
  );
});
</script>

<!-- Non-scoped: the Ionic modal renders in a portal outside this component,
     so the centering / glass host styles must be global. Any page can reuse
     this by giving its <ion-modal> css-class="update-required-modal". -->
<style lang="scss">
ion-modal.update-required-modal {
  --background: transparent;
  --width: 88%;
  --max-width: 380px;
  --height: auto;
  --max-height: 90vh;
  --border-radius: var(--syntry-radius-modal);
  --box-shadow: none;
  --backdrop-opacity: 0.32;
  align-items: center;
  justify-content: center;
}

ion-modal.update-required-modal .ion-page {
  position: relative;
  contain: content;
  background: transparent;
  height: auto;
}
</style>

<style scoped lang="scss">
.update-required-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--sp-7) var(--sp-6) var(--sp-6);
  /* Frosted-glass surface — matches ConfirmModal / InstallAppModal. */
  background: rgba(var(--syntry-surface-rgb), 0.82);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(var(--syntry-surface-rgb), 0.55);
  border-radius: var(--syntry-radius-modal);
  box-shadow: var(--syntry-shadow-elevated);

  &__icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    margin-bottom: var(--sp-5);
    background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
  }

  &__title {
    font-size: var(--syntry-fs-h2);
    font-weight: 800;
    color: var(--syntry-text-strong);
    margin: 0 0 var(--sp-2);
    line-height: 1.3;
  }

  &__message {
    font-size: var(--syntry-fs-body);
    color: var(--syntry-text-muted);
    line-height: 1.5;
    margin: 0 0 var(--sp-6);
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  &__actions {
    display: flex;
    gap: var(--sp-3);
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    border-radius: var(--syntry-radius-button);
    font-size: var(--syntry-fs-body);
    font-weight: 700;
    font-family: var(--syntry-font);
    cursor: pointer;
    border: none;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 150ms ease;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:not(:disabled):active { opacity: 0.85; }

    &--outline {
      background: transparent;
      border: 2px solid var(--syntry-border);
      color: var(--syntry-text);
    }

    &--primary {
      background: var(--syntry-primary);
      color: #ffffff;
      box-shadow: var(--syntry-shadow-button);
    }
  }

  &__spinner {
    width: 22px;
    height: 22px;
    color: #ffffff;
  }
}
</style>
